import type { ReactNode } from 'react';
import {
    CountedTextInput,
    FormField,
    InputGroup,
    Paragraph,
    TextareaInput,
} from '@george-labs.com/design-system';
import type { CountedTextInputProps } from '@george-labs.com/design-system/types/components/Input';

import { useField } from '@luna/form2';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

interface Props<FormValues>
    extends FormFieldProps<FormValues>,
        Omit<CountedTextInputProps<HTMLTextAreaElement>, 'input'> {
    autoFocus?: boolean;
    prepend?: ReactNode;
    append?: ReactNode;
    rows?: number;
    maxLength?: number;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
}

const LABEL_COLUMN_WIDTH = 4;

export const FormCountedTextAreaField = <FormValues = void,>({
    label,
    placeholder,
    'data-cy': dataCy,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    labelPosition,
    fieldName,
    required,
    disabled,
    warning,
    info,
    alert,
    autoFocus,
    append,
    prepend,
    description,
    maxChars,
    maxLength,
    message,
    limitInputToMaxChars,
    rows,
    isErrorVisible = true,
    addon,
    shouldClearOnUnmount,
}: Props<FormValues>) => {
    const [fieldProps, { error, touched }] = useField(fieldName, { shouldClearOnUnmount });

    const isFieldErrorVisible = shouldShowError(isErrorVisible, touched, error);

    return (
        <FormField
            description={description}
            required={required}
            input={
                <InputGroup disabled={disabled} append={append} prepend={prepend}>
                    <CountedTextInput
                        maxChars={maxChars}
                        message={message}
                        limitInputToMaxChars={limitInputToMaxChars}
                        input={
                            <TextareaInput
                                placeholder={placeholder}
                                rows={rows}
                                autoFocus={autoFocus}
                                data-cy={dataCy}
                                maxLength={maxLength}
                                {...fieldProps}
                            />
                        }
                    />
                </InputGroup>
            }
            label={label}
            error={
                isFieldErrorVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            labelColumnWidth={labelColumnWidth}
            labelPosition={labelPosition}
            data-cy={dataCy}
            disabled={disabled}
            warning={warning}
            info={info}
            alert={alert}
            addon={addon}
        />
    );
};

FormCountedTextAreaField.LABEL_POSITION = FormField.LABEL_POSITION;
